package com.tovtalk.data.analytics

/**
 * Public interface for video analytics tracking.
 *
 * UI code depends only on this interface; [AnalyticsClientImpl] is internal.
 * Web/JS receives [NoOpAnalyticsClient] from DI instead of the real impl.
 */
interface AnalyticsClient {

    // ── Video player lifecycle ─────────────────────────────────────────────

    suspend fun onVideoStarted(postId: Long, videoDurationMs: Long, startedFrom: StartedFrom)
    suspend fun onVideoProgress(postId: Long, positionMs: Long, isPlaying: Boolean, soundOn: Boolean)
    suspend fun onVideoPaused(postId: Long, positionMs: Long)
    suspend fun onVideoResumed(postId: Long, positionMs: Long)
    suspend fun onVideoSeeked(postId: Long, fromMs: Long, toMs: Long)
    suspend fun onVideoLooped(postId: Long)
    suspend fun onVideoEnded(postId: Long, exitReason: ExitReason)

    // ── Interactions ───────────────────────────────────────────────────────

    suspend fun trackInteraction(
        postId: Long,
        type: InteractionType,
        payload: Map<String, String>? = null,
        positionMs: Long? = null,
    )

    /**
     * Critical events (share/follow/report/comment) — also immediately flushes
     * the entire queue so the event is not lost.
     */
    suspend fun trackCriticalInteraction(
        postId: Long,
        type: InteractionType,
        payload: Map<String, String>? = null,
        positionMs: Long? = null,
    )

    // ── App lifecycle ──────────────────────────────────────────────────────

    suspend fun onAppForeground()
    suspend fun onAppBackground()

    // ── Lifecycle management ───────────────────────────────────────────────

    /** Called from app init — restores persisted queue from disk and starts flusher. */
    suspend fun initialize()

    /** Update subject key when user logs in/out. Format: `"u:$userId"` or `"g:$guestId"`. */
    fun setSubject(subjectKey: String)

    // ── Foreground post tracking (for screenshot detection) ────────────────

    /** Call from feed item when it becomes the foreground post. Pass null when leaving. */
    fun setCurrentPost(postId: Long?)

    /** Returns the id of the currently-visible foreground post, or null if none. */
    fun getCurrentPost(): Long?
}
